import { Component, forwardRef, ViewChild, Input, ChangeDetectorRef } from '@angular/core';
import { PlatformApiService, ResultFilter, Results } from '../../services/platform-api.service';
import { FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { WizardScreenComponent } from 'src/app/shared/wizard.component';
import { DataTableComponent } from 'src/app/shared/tile-data-table/tile-data-table.component';
import { environment } from 'src/environments/environment';
import { Account, User, Role, UserCredential, RoleType } from 'src/app/models/AccountModel';
import { SessionService } from 'src/app/services/session.service';
import { InfiniteScrollOption } from 'src/app/shared/infinite-scroll-select.component';

@Component({
  selector: 'cw-ci-account-users',
  templateUrl: './account-users.component.html',
  styleUrls: ['./account-users.component.scss'],
  providers: [{provide: WizardScreenComponent, useExisting: forwardRef(() => AccountUsersComponent)}]
})
export class AccountUsersComponent extends WizardScreenComponent {
  @ViewChild(DataTableComponent) userDataTable: DataTableComponent;
  userList: Results = new Results([],0);
  @Input()
  account: Account;
  selectedUser: User;
  formEnabled = true;
  RoleType = RoleType;
  disableDelete = true;

  constructor(private _apiService: PlatformApiService, public sessionService: SessionService, private changeDetectorRef: ChangeDetectorRef) {
    super();
    this.form = new FormGroup ({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required]),
      directEnabled: new FormControl(''),
      directMfaEnabled: new FormControl(''),
      ssoEnabled: new FormControl(''),
      manageEnabled: new FormControl(''),
      manageUserDropdown: new FormControl('', [Validators.required]),
    });
   this.form.controls.directMfaEnabled.disable();
  }


  onLoad() {
    this.disableDelete = !this.sessionService.hasRole(RoleType.ADMIN) && (this.account.id != this.sessionService.getCurrentAccount().id && this.account.parentId != this.sessionService.getCurrentAccount().id);
    // If this component is the first item displayed when the wizard opens, it will
    // throw an ExpressionChangedAfterItHasBeenCheckedError due to manipulating the form controls. This
    // is because in the initial load scenario, change detection has completed for the cycle before angular
    // gets to this method. We detach, make our changes, reattach, and trigger detection manually.
    this.changeDetectorRef.detach()

    if(!this.sessionService.hasRole(RoleType.ADMIN) && this.sessionService.isAccountType('SYSTEM')) {
      this.form.disable();
    }

    this.form.reset();
    this.changeDetectorRef.detectChanges();
    this.userDataTable.resetDataTable();
    this.userDataTable.executeLoadItems();
    this.changeDetectorRef.reattach();
    this.changeDetectorRef.detectChanges();
    super.onLoad();
}

  preClose() {
    this.unselectUser();
  }

  onCancel() {
    super.onCancel(false);
  }

  loadUsers(filter = new ResultFilter()) {
    this.userDataTable.showProcessing();

    this._apiService.getAccountUsers(this.account.id, filter).subscribe(
      results => {
        this.userList = results;
        console.log(this.userList);
      }
    );
  }

  isSelectable(user) {
    return true;
  }

  selectUser(user: User) {
    this.selectedUser = user;
    this.form.reset();
    this.form.controls.firstName.setValue(this.selectedUser.firstName);
    this.form.controls.lastName.setValue(this.selectedUser.lastName);
    this.form.controls.email.setValue(this.selectedUser.email);
    this.form.controls.directEnabled.setValue(this.selectedUser.directEnabled);
    if(this.selectedUser.directEnabled){
      const creds = this.selectedUser.credentials.filter(cred=> {return cred.issuer == environment.cognitoJwtIssuer});
      this.form.controls.directMfaEnabled.setValue(creds[0].mfaEnabled);
      if(!creds[0].mfaEnabled) {
        this.form.controls.directMfaEnabled.disable();
      } else {
        this.form.controls.directMfaEnabled.enable();
      }
    }
    this.form.controls.ssoEnabled.setValue(this.selectedUser.ssoEnabled);
    this.form.controls.manageEnabled.setValue(this.selectedUser.manageEnabled);
    if(this.selectedUser.manageEnabled) {
      this.form.controls.manageUserDropdown.setValidators([Validators.required]);
    } else {
      this.form.controls.manageUserDropdown.setValidators(null);
    }

    if(this.account.manageIntegration && this.account.manageIntegration.isEnabled && this.selectedUser.manageEnabled) {
      const creds = this.selectedUser.credentials.filter(cred=> {return cred.issuer == 'CONNECTWISE'});
      this.form.controls.manageUserDropdown.setValue(new InfiniteScrollOption(this.selectedUser.firstName + ' ' + this.selectedUser.lastName, creds[0].userId, false, false, true));
    }

    if (this.selectedUser.id) {
      this.form.controls.email.disable();
    } else {
      this.form.controls.email.enable();
    }

    this.notifyWizardComponent('SHOW_ACTION_BUTTONS');
  }

  unselectUser() {
    this.selectedUser = undefined;
    this.form.reset();
    this.notifyWizardComponent('HIDE_ACTION_BUTTONS');
  }

  newUser(event?) {
    this.selectUser(new User());
  }

  onSave() {
    if (this.isValid()) {
      this.showSpinner('Saving user...');

      this.selectedUser.firstName = this.form.value.firstName;
      this.selectedUser.lastName = this.form.value.lastName;
      if(!this.selectedUser.id) {
        this.selectedUser.email = this.form.controls.email.value;
        this.selectedUser.roles = [new Role({name:RoleType.ADMIN}), new Role({name:RoleType.USER})]
      }

      if(this.form.value.directEnabled) {
        if(!this.selectedUser.directEnabled){
          if(this.selectedUser.credentials == null) {
            this.selectedUser.credentials = new Array();
          }
          this.selectedUser.credentials.push(new UserCredential({issuer: environment.cognitoJwtIssuer}));
        } else {
          const creds = this.selectedUser.credentials.filter(cred=> {return cred.issuer == environment.cognitoJwtIssuer});
          if(creds[0].mfaEnabled && !this.form.value.directMfaEnabled) {
            creds[0].mfaEnabled = false;
          // } else if (this.form.value.directMfaEnabled) {
          //   creds[0].mfaEnabled = true;
          }
        }
      }
      if(this.account.manageIntegration && this.account.manageIntegration.isEnabled) {
        if(this.form.value.manageEnabled) {
          if(this.selectedUser.manageEnabled){
            const manageCred = this.selectedUser.credentials.filter(cred => {return cred.issuer == 'CONNECTWISE'});
            manageCred[0].externalId = this.form.controls.manageUserDropdown.value.value;
          } else {
            const manageCredential = new UserCredential();
            manageCredential.issuer = 'CONNECTWISE';
            manageCredential.externalId = this.form.controls.manageUserDropdown.value.value;

            if(this.selectedUser.credentials == null) {
              this.selectedUser.credentials = new Array();
            }
            this.selectedUser.credentials.push(manageCredential);
          }
        } else if(!this.form.value.manageEnabled && this.selectedUser.manageEnabled) {
          this.selectedUser.credentials.forEach((cred,index) =>{if(cred.issuer == 'CONNECTWISE') {this.selectedUser.credentials.splice(index,1)}});
        }
      }

      this._apiService.saveUser(this.account.id, this.selectedUser.id, this.selectedUser).subscribe(
        resp => {
          this.unselectUser();
          this.hideSpinner();
          this.onLoad();
        }, err=> {
          this.hideSpinner();
          if(err.error.code == 'USER_EXISTS') {
            this.form.controls.email.setErrors({inUse: true}, {emitEvent:true});
          } else {
            this.showMessage('An error occurred while saving.')
            console.log('User save error', err);
          }
        }
      );
    }
  }

  resendInvitation(usr) {
    this._apiService.resendInvitation(usr.accountId, usr.id).subscribe(
      resp => {
        if(resp.success) {
          this.showMessage('Sent successfully');
        }
        else {
          this.showMessage('Failed to send: '+resp.message);
        }
      }
    );

  }

  deleteUser(usr) {
    if(usr.id == this.sessionService.getCurrentUser().id) {
      this.showPrompt('You cannot delete your own user.', () => {});
    } else {
      this.showPrompt('You are about to delete a user, do you want to continue?',(shouldDelete) => {
        if(shouldDelete) {
          this.showSpinner('Deleting user...');
          this._apiService.deleteUser(this.account.id, usr.id).subscribe(
            resp => {
              this.unselectUser();
              this.hideSpinner();
              this.onLoad();
            }, err => {
              if(err.error.code == 'RecordInUse') {
                this.showMessage('You cannot delete your own user.');
              } else {
                this.showMessage('Failed to delete user. Please contact and administrator.');
              }
            }
          );
        }
      }, [{label: 'Yes', value:true, color:'primary'}]);
    }
  }


  handleEvent(event) {
    switch (event.state) {
      case 'ONSELECT':
        this.showPrompt('Would you like to update the users information to match Manage?', (result)=>{
          if(result) {
            this.form.controls.firstName.setValue(event.selection.option.value.data.firstName);
            this.form.controls.firstName.markAsTouched();
            this.form.controls.lastName.setValue(event.selection.option.value.data.lastName);
            this.form.controls.lastName.markAsTouched();

            // set email only if its a new user
            if(!this.selectedUser.id) {
              let email;
              switch(event.selection.option.value.data.defaultEmail){
                case 'home':
                  email = event.selection.option.value.data.homeEmail;
                  break;
                case 'mobile':
                  email = event.selection.option.value.data.mobileEmail;
                  break;
                case 'office':
                default:
                  email = event.selection.option.value.data.officeEmail;
                  break;
              }
              this.form.controls.email.setValue(email);
              this.form.controls.email.markAsTouched();
            }
          }
        });
        break;
      case 'MANAGE_INTEGRATION_CLICKED':
        if(this.form.value.manageEnabled) {
          // box has been unchecked
          this.form.controls.manageUserDropdown.setValidators(null);
        } else {
          // box has been checked
          this.form.controls.manageUserDropdown.setValidators([Validators.required]);
        }
        this.form.controls.manageUserDropdown.updateValueAndValidity();
        break;
    }
  }

  forbiddenNames(control: AbstractControl) {
    const selection: any = control.value;
    if (typeof selection === 'string') {
      return  this.form.controls.manageUserDropdown.setErrors({incorrect: true});
    }
    return null
  }
}